/**
 * The touch/keyboard HUD for the walkable port (Onda 6). A left thumbstick walks the avatar
 * (omnidirectional), a right cluster fires the free social emotes (wave/dance) + a contextual "Trade"
 * button (near a stall) + a contextual "Sit" button (near a seat) + a "Leave Port" button, and a coin
 * chip shows your wealth. Keyboard-mapped (WASD/arrows to walk, 1/2 emote, F sit, E trade, L/Esc leave)
 * for desktop + headless Chrome. Sit is a contextual action (needs a real seat), not a free emote.
 */
import { EMOTE_ICONS, EMOTE_LABELS, FREE_EMOTE_IDS } from '@piratas/protocol';

const CSS = `
#porthud{ position:fixed; inset:0; z-index:30; pointer-events:none; font-family:inherit; }
#porthud .pcoin{ position:fixed; top:calc(env(safe-area-inset-top,8px) + 12px); left:12px; padding:8px 14px; border-radius:12px;
  background:linear-gradient(165deg,rgba(16,30,46,.86),rgba(6,14,26,.9)); border:1px solid rgba(232,192,116,.34); color:#f2e2b8;
  font-weight:700; font-size:15px; box-shadow:0 6px 18px rgba(0,8,16,.5); }
#porthud .pstick{ position:fixed; bottom:calc(env(safe-area-inset-bottom,10px) + 26px); left:26px; width:132px; height:132px; border-radius:50%;
  background:radial-gradient(circle,rgba(12,24,38,.55),rgba(6,14,26,.32)); border:1px solid rgba(232,192,116,.28); pointer-events:auto; touch-action:none; }
#porthud .pnub{ position:absolute; width:56px; height:56px; border-radius:50%; left:38px; top:38px;
  background:radial-gradient(circle at 40% 35%,rgba(240,220,170,.95),rgba(180,140,80,.9)); box-shadow:0 4px 12px rgba(0,0,0,.5); }
#porthud .pbtns{ position:fixed; bottom:calc(env(safe-area-inset-bottom,10px) + 26px); right:20px; display:flex; flex-direction:column; gap:10px; align-items:flex-end; pointer-events:auto; }
#porthud .prow{ display:flex; gap:8px; }
#porthud button{ pointer-events:auto; border:1px solid rgba(232,192,116,.34); border-radius:13px; cursor:pointer; color:#eef6ff;
  background:linear-gradient(165deg,rgba(16,30,46,.9),rgba(6,14,26,.94)); box-shadow:0 5px 14px rgba(0,8,16,.5); font-size:20px; }
#porthud .pemote{ width:52px; height:52px; }
#porthud .ptrade{ padding:12px 18px; font-size:15px; font-weight:700; display:none; }
#porthud .pplay{ padding:12px 18px; font-size:15px; font-weight:800; display:none; color:#2a1c06;
  background:linear-gradient(180deg,#e2be5a,#b98a2c); border-color:#8a6a1e; box-shadow:0 5px 14px rgba(0,8,16,.5), inset 0 1px 0 rgba(255,255,255,.4); }
#porthud .phrow{ display:flex; gap:8px; }
#porthud .pdecorate{ padding:12px 16px; font-size:14px; font-weight:800; display:none; color:#2a1c06;
  background:linear-gradient(180deg,#8fd0a0,#4f9a68); border-color:#2f6a44; box-shadow:0 5px 14px rgba(0,8,16,.5), inset 0 1px 0 rgba(255,255,255,.4); }
#porthud .psit{ padding:12px 16px; font-size:14px; font-weight:800; display:none; color:#f2e2b8;
  background:linear-gradient(180deg,rgba(40,52,30,.95),rgba(24,34,16,.96)); }
#porthud .popen{ padding:12px 14px; font-size:13.5px; font-weight:700; display:none; color:#f2d98c;
  background:linear-gradient(180deg,rgba(58,42,18,.94),rgba(36,26,10,.95)); }
#porthud .pkick{ padding:12px 14px; font-size:13.5px; font-weight:700; display:none; color:#ffd0c0;
  background:linear-gradient(180deg,rgba(90,30,24,.94),rgba(60,18,14,.95)); border-color:rgba(220,120,100,.4); }
#porthud .pleave{ padding:11px 16px; font-size:14px; font-weight:700; background:linear-gradient(180deg,rgba(58,42,18,.94),rgba(36,26,10,.95)); color:#f2d98c; }
#porthud .phint{ position:fixed; bottom:calc(env(safe-area-inset-bottom,10px) + 176px); left:50%; transform:translateX(-50%);
  padding:6px 14px; border-radius:11px; background:rgba(6,16,20,.85); color:#f2e2b8; font-size:13px; font-weight:700; display:none; pointer-events:none;
  border:1px solid rgba(232,192,116,.3); }
`;

export class PortHud {
  onEmote: ((id: number) => void) | null = null;
  onTrade: (() => void) | null = null;
  onPlay: (() => void) | null = null;
  onLeave: (() => void) | null = null;
  // wave 8 (house-only) — never wired in a normal port, so the buttons stay hidden there
  onDecorate: (() => void) | null = null;
  onToggleOpen: ((next: boolean) => void) | null = null;
  onKick: ((avatarId: number) => void) | null = null;
  onSit: (() => void) | null = null;

  private root: HTMLElement;
  private nub: HTMLElement;
  private coin: HTMLElement;
  private trade: HTMLButtonElement;
  private play: HTMLButtonElement;
  private hint: HTMLElement;
  private decorate: HTMLButtonElement;
  private sit: HTMLButtonElement;
  private openBtn: HTMLButtonElement;
  private kickBtn: HTMLButtonElement;
  private openToClan = false;
  private kickTarget = -1;
  private mx = 0; private my = 0;
  private stickId = -1;
  private keys = new Set<string>();
  private keyHandler: (e: KeyboardEvent, down: boolean) => void;

  constructor(overlay: HTMLElement) {
    if (!document.getElementById('porthud-style')) {
      const st = document.createElement('style'); st.id = 'porthud-style'; st.textContent = CSS; document.head.appendChild(st);
    }
    this.root = document.createElement('div'); this.root.id = 'porthud';
    const emoteRow = FREE_EMOTE_IDS.map((id) => `<button class="pemote" data-emote="${id}" title="${EMOTE_LABELS[id]}">${EMOTE_ICONS[id]}</button>`).join('');
    this.root.innerHTML = `
      <div class="pcoin">🪙 <span class="pcoinv">0</span></div>
      <div class="pstick"><div class="pnub"></div></div>
      <div class="phint">Walk to the dock to set sail ⚓</div>
      <div class="pbtns">
        <button class="pplay">🎲 Play</button>
        <button class="ptrade">🛒 Trade</button>
        <button class="psit">🪑 Sit</button>
        <button class="pdecorate">🛠 Decorate</button>
        <div class="phrow">
          <button class="pkick">👢 Boot</button>
          <button class="popen">⚔ Armada</button>
        </div>
        <div class="prow">${emoteRow}</div>
        <button class="pleave" id="pleave">⚓ Leave Port</button>
      </div>`;
    overlay.appendChild(this.root);
    const stick = this.root.querySelector('.pstick') as HTMLElement;
    this.nub = this.root.querySelector('.pnub') as HTMLElement;
    this.coin = this.root.querySelector('.pcoinv') as HTMLElement;
    this.trade = this.root.querySelector('.ptrade') as HTMLButtonElement;
    this.play = this.root.querySelector('.pplay') as HTMLButtonElement;
    this.hint = this.root.querySelector('.phint') as HTMLElement;
    this.decorate = this.root.querySelector('.pdecorate') as HTMLButtonElement;
    this.sit = this.root.querySelector('.psit') as HTMLButtonElement;
    this.openBtn = this.root.querySelector('.popen') as HTMLButtonElement;
    this.kickBtn = this.root.querySelector('.pkick') as HTMLButtonElement;

    const setStick = (cx: number, cy: number) => {
      const r = stick.getBoundingClientRect();
      const dx = cx - (r.left + r.width / 2), dy = cy - (r.top + r.height / 2);
      const max = r.width / 2 - 8;
      const d = Math.hypot(dx, dy) || 1;
      const cl = Math.min(1, d / max);
      const nx = (dx / d) * cl, ny = (dy / d) * cl;
      this.mx = nx; this.my = -ny; // screen-down is -y (north is +y in the sim plane)
      this.nub.style.left = `${38 + nx * max}px`; this.nub.style.top = `${38 + ny * max}px`;
    };
    const reset = () => { this.mx = 0; this.my = 0; this.nub.style.left = '38px'; this.nub.style.top = '38px'; };
    stick.addEventListener('pointerdown', (e) => { e.preventDefault(); this.stickId = e.pointerId; stick.setPointerCapture(e.pointerId); setStick(e.clientX, e.clientY); });
    stick.addEventListener('pointermove', (e) => { if (e.pointerId === this.stickId) setStick(e.clientX, e.clientY); });
    const end = (e: PointerEvent) => { if (e.pointerId === this.stickId) { this.stickId = -1; reset(); } };
    stick.addEventListener('pointerup', end); stick.addEventListener('pointercancel', end);

    this.root.querySelectorAll('[data-emote]').forEach((b) => b.addEventListener('click', () => this.onEmote?.(Number((b as HTMLElement).dataset.emote))));
    this.trade.addEventListener('click', () => this.onTrade?.());
    this.play.addEventListener('click', () => this.onPlay?.());
    this.decorate.addEventListener('click', () => this.onDecorate?.());
    this.sit.addEventListener('click', () => this.onSit?.());
    this.openBtn.addEventListener('click', () => this.onToggleOpen?.(!this.openToClan));
    this.kickBtn.addEventListener('click', () => { if (this.kickTarget >= 0) this.onKick?.(this.kickTarget); });
    (this.root.querySelector('.pleave') as HTMLElement).addEventListener('click', () => this.onLeave?.());

    this.keyHandler = (e, down) => {
      const k = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) { if (down) this.keys.add(k); else this.keys.delete(k); e.preventDefault(); }
      if (down && (k === 'e')) { if (this.play.style.display !== 'none') this.onPlay?.(); else if (this.trade.style.display !== 'none') this.onTrade?.(); }
      if (down && (k === 'l' || k === 'escape')) this.onLeave?.();
      if (down && k === 'f' && this.sit.style.display !== 'none') this.onSit?.(); // house: sit/stand (needs a seat)
      if (down && (k === '1' || k === '2')) { const id = FREE_EMOTE_IDS[Number(k) - 1]; if (id !== undefined) this.onEmote?.(id); }
    };
    window.addEventListener('keydown', this.kd); window.addEventListener('keyup', this.ku);
  }

  private kd = (e: KeyboardEvent) => this.keyHandler(e, true);
  private ku = (e: KeyboardEvent) => this.keyHandler(e, false);

  /** The current move vector (-1..1). Merges the thumbstick with keyboard (WASD/arrows). */
  sampleMove(): { moveX: number; moveY: number } {
    let mx = this.mx, my = this.my;
    if (this.keys.size) {
      let kx = 0, ky = 0;
      if (this.keys.has('a') || this.keys.has('arrowleft')) kx -= 1;
      if (this.keys.has('d') || this.keys.has('arrowright')) kx += 1;
      if (this.keys.has('w') || this.keys.has('arrowup')) ky += 1;   // up = north (+y)
      if (this.keys.has('s') || this.keys.has('arrowdown')) ky -= 1;
      if (kx || ky) { mx = kx; my = ky; }
    }
    return { moveX: mx, moveY: my };
  }

  setWealth(doubloons: number): void { this.coin.textContent = Math.round(doubloons).toLocaleString(); }
  setTradeAvailable(v: boolean): void { this.trade.style.display = v ? 'block' : 'none'; }
  /** Onda 7: show/hide the contextual casino/auction/clan "Play" button with a game-specific label. */
  setPlay(label: string | null): void { this.play.style.display = label ? 'block' : 'none'; if (label) this.play.textContent = label; }
  setHint(text: string | null): void { this.hint.style.display = text ? 'block' : 'none'; if (text) this.hint.textContent = text; }

  /* ---- wave 8: house-only affordances (Trade/Play stay hidden in a house; owner gets Decorate + Armada) ---- */
  /** Enter/leave house mode. `owner` gates the 🛠 Decorate + ⚔ Armada toggle to the house owner. */
  setHouseMode(on: boolean, owner: boolean): void {
    if (on) { this.trade.style.display = 'none'; this.play.style.display = 'none'; }
    this.decorate.style.display = on && owner ? 'block' : 'none';
    this.openBtn.style.display = on && owner ? 'block' : 'none';
    if (!on) { this.kickBtn.style.display = 'none'; this.kickTarget = -1; this.sit.style.display = 'none'; }
    (this.root.querySelector('.pleave') as HTMLElement).textContent = on ? '⚓ Set Sail' : '⚓ Leave Port';
  }
  /** Interior Vivo — show/label the contextual Sit/Stand button. `show` is true whenever the captain is on
   *  foot in a house (or currently seated); pressing it away from a seat toasts (see main). Hidden in the port
   *  (no furniture) via setHouseMode(false). */
  setSittable(show: boolean, seated: boolean): void {
    this.sit.style.display = (show || seated) ? 'block' : 'none';
    this.sit.textContent = seated ? '🧍 Stand' : '🪑 Sit';
  }
  /** Reflect + label the "Open to Armada" toggle (owner only). */
  setOpenToClan(open: boolean): void { this.openToClan = open; this.openBtn.textContent = open ? '⚔ Armada: On' : '⚔ Armada: Off'; }
  /** Show/hide the owner's contextual Boot (kick) button, targeting a specific visitor avatar (-1 = hide). */
  setKickable(avatarId: number): void { this.kickTarget = avatarId; this.kickBtn.style.display = avatarId >= 0 ? 'block' : 'none'; }

  show(): void { this.root.style.display = 'block'; }
  hide(): void { this.root.style.display = 'none'; }
  destroy(): void {
    window.removeEventListener('keydown', this.kd); window.removeEventListener('keyup', this.ku);
    this.root.remove();
  }
}
